import { Engine, Mesh, MultiMaterial, SceneOptions, StandardMaterial } from '@babylonjs/core';
import { ICamera } from 'Framework/Components/Controls/XView/Cameras';
import { IPanoramaConfiguration } from 'Framework/Components/Controls/XView/Core';
import { LazyTextureMaterial, TextureMaterial } from 'Framework/Components/Controls/XView/Materials';
import { MeshBuilder } from 'Framework/Components/Controls/XView/MeshBuilder';
import { BaseRenderer } from 'Framework/Components/Controls/XView/Renderers/BaseRenderer';
import { UIElement } from 'Framework/Components/UIElement';

export class Cube360Renderer extends BaseRenderer<IPanoramaConfiguration> {
    public constructor(engine: Engine, camera: ICamera, options?: SceneOptions) {
        super(engine, camera, options);

        this.registerBeforeRender(() => this._cullingLevel());
    }

    protected override onLoad(configuration: IPanoramaConfiguration): void {
        this.camera.lookTo(configuration.startOrientation);
        this._levelCubeMeshes = new Array<Mesh>();

        for (let level = 1; level <= configuration.MaxLevel; level++) {
            let panoramaLevel = configuration.Levels[level - 1];
            let faceSize = panoramaLevel.ImageWidth;
            let subdivisions = faceSize / panoramaLevel.TileSize;

            let mesh = MeshBuilder.CreateCubeStrip(`CubeStrip_${level}`, { width: faceSize, height: faceSize, subdivisions: subdivisions }, this);
            this._levelCubeMeshes.push(mesh);
            mesh.actionManager = this.actionManager;

            let loadedTextures: Array<Promise<boolean>>;

            if (level === 1) {
                loadedTextures = new Array<Promise<boolean>>();
            }

            let multimat = new MultiMaterial('multi_mat_' + level, this);

            mesh.material = multimat;
            let subMeshIndex = 0;

            for (let cubeSide of MeshBuilder.cubeSides) {
                for (let y = subdivisions - 1; y >= 0; y -= 1) {
                    for (let x = 0; x < subdivisions; x++) {
                        let url = configuration.Url
                            .replace('%lvl', level.toString())
                            .replace('%s', cubeSide.Label)
                            .replace('%h', x.toString())
                            .replace('%v', y.toString());

                        const materialName = `mat_${level}_${cubeSide.Label}_${x}_${y}`;

                        let material: StandardMaterial = level == 1
                            ? new TextureMaterial(materialName, this, url)
                            : new LazyTextureMaterial(materialName, this, url, mesh.subMeshes[subMeshIndex]);

                        multimat.subMaterials.push(material);
                        subMeshIndex++;
                    }
                }
            }

            if (level > 1) {
                mesh.isVisible = false;
            }
            else {
                const _ = Promise
                    .all(loadedTextures)
                    .then(t => {
                        if (t.every(isLoaded => isLoaded === true)) {
                            UIElement.Bus.$emit('preview-loaded');
                        }
                    });
            }
        }
    }

    private _cullingLevel(): void {
        if (!this._configuration) {
            return;
        }

        let maxLevel = this._configuration.MaxLevel;

        const percent = ((this.camera.maxFov - this.camera.fov) / this.camera.maxFov);
        let currentLayerDepth = Math.trunc(maxLevel * percent);

        if (currentLayerDepth < 1) {
            currentLayerDepth = 1;
        }

        if (currentLayerDepth >= maxLevel) {
            currentLayerDepth = maxLevel - 1;
        }

        for (let level = 0; level < maxLevel; level++) {
            let panoramaLevel = this._levelCubeMeshes[level];

            panoramaLevel.isVisible = level == 0 || currentLayerDepth == level;
        }
    }

    private _levelCubeMeshes: Array<Mesh>;
}
